import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Drawer,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Box,
  Divider,
  ListItemButton,
  ListItemIcon,
  Typography,
  Avatar,
  Tooltip,
  ListSubheader,
} from '@mui/material';
import { styled } from '@mui/system';
import {
  Menu as MenuIcon,
  Home as HomeIcon,
  ContactSupport as ContactIcon,
  AdminPanelSettings as AdminIcon,
  ExitToApp as ExitIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ListAlt as ListAltIcon,
  Settings as SettingsIcon,
  ChecklistRtl as ChecklistRtlIcon,
  AutoAwesome as AutoAwesomeIcon,
  Assessment as AssessmentIcon,
  Rule as RuleIcon,
  CompareArrows as CompareIcon,
} from '@mui/icons-material';
import logo from '../img/logo.png';

const drawerWidth = 260;
const drawerWidthMini = 72;

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'space-between',
}));

const Navbar = ({ handleLogout, isAdmin, userModulos, username, nome_empresa, open, handleDrawerOpen, handleDrawerClose }) => {
  const theme = useTheme();
  const location = useLocation();

  const isAdminOrHasModulo = (modulo) => isAdmin || (userModulos && userModulos.includes(modulo));

  const initialMenuGroups = [
    {
      title: 'SISTEMA',
      items: [
        { text: 'Home', icon: <HomeIcon />, path: '/', alwaysVisible: true },
        { text: 'Configurações', icon: <SettingsIcon />, path: '/configuracoes', modulo: 'configuracoes' },
        { text: 'Suporte', icon: <ContactIcon />, path: '/contato', modulo: 'suporte' },
      ]
    },
    {
      title: 'FATURAMENTO',
      modulo: 'cadastros_tiss',
      items: [
        { text: 'Auditoria Inteligente', icon: <AssessmentIcon />, path: '/auditoria-tiss' },
        { text: 'Auditoria inteligente Custom', icon: <AutoAwesomeIcon />, path: '/auditoria-custom' },
        { text: 'Comparador Global', icon: <CompareIcon />, path: '/comparador-procedimentos' },
        { text: 'Regras de Auditoria', icon: <RuleIcon />, path: '/regras-auditoria' },
        { text: 'Cadastros Faturamento', icon: <SettingsIcon />, path: '/cadastros-tiss' },
      ]
    },
    {
      title: 'AUDITORIA SUS',
      modulo: 'cadastros_tiss',
      items: [
        { text: 'Auditoria AIH', icon: <AssessmentIcon />, path: '/auditoria-sus' },
      ]
    }
  ];

  const menuGroups = initialMenuGroups
    .filter(group => !group.modulo || isAdminOrHasModulo(group.modulo))
    .map(group => ({
      ...group,
      items: group.items.filter(item => item.alwaysVisible || !item.modulo || isAdminOrHasModulo(item.modulo))
    }))
    .filter(group => group.items.length > 0);

  if (isAdmin) {
    menuGroups.push({
      title: 'ADMINISTRAÇÃO',
      items: [
        { text: 'Admin', icon: <AdminIcon />, path: '/admin' },
        { text: 'Logs', icon: <ListAltIcon />, path: '/logs' },
      ]
    });
  }

  const bgColor = '#0f172a'; // Deep slate (SACH style)
  const activeGradient = 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)';
  const itemBorderRadius = '12px';

  return (
    <Drawer
      variant="permanent"
      open={open}
      sx={{
        width: open ? drawerWidth : drawerWidthMini,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: open ? drawerWidth : drawerWidthMini,
          boxSizing: 'border-box',
          overflowX: 'hidden',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          backgroundColor: bgColor,
          color: '#f8fafc',
          borderRight: 'none',
          boxShadow: '4px 0 20px rgba(0,0,0,0.25)',
        },
      }}
    >
      <DrawerHeader sx={{ px: 2, pt: 2, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden', whiteSpace: 'nowrap' }}>
          <img
            src={logo}
            alt="Logo"
            style={{
              height: 32,
              filter: 'brightness(0) invert(1)',
              transition: 'all 0.3s ease'
            }}
          />
          {open && (
            <Typography
              variant="h6"
              noWrap
              sx={{
                fontWeight: 800,
                ml: 1.5,
                fontSize: '1.25rem',
                letterSpacing: '-0.02em',
                background: 'linear-gradient(135deg, #fff 0%, #cbd5e1 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              SACCI
            </Typography>
          )}
        </Box>
        <IconButton
          onClick={open ? handleDrawerClose : handleDrawerOpen}
          sx={{ color: '#94a3b8', '&:hover': { color: '#fff' } }}
        >
          {open ? (theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />) : <MenuIcon />}
        </IconButton>
      </DrawerHeader>

      <List sx={{ px: 1.5, mt: 1 }}>
        {menuGroups.map((group, index) => (
          <React.Fragment key={group.title}>
            {open && (
              <ListSubheader
                component="div"
                sx={{
                  bgcolor: 'transparent',
                  color: '#64748b',
                  lineHeight: '32px',
                  pt: 2,
                  pb: 0.5,
                  fontSize: '0.7rem',
                  letterSpacing: '0.1em',
                  fontWeight: 800,
                  transition: 'opacity 0.3s ease',
                }}
              >
                {group.title}
              </ListSubheader>
            )}
            {!open && index > 0 && <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)', my: 2 }} />}
            {group.items.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <ListItem key={item.text} disablePadding sx={{ display: 'block', mb: 0.5 }}>
                  <Tooltip title={!open ? item.text : ''} placement="right">
                    <ListItemButton
                      component={Link}
                      to={item.path}
                      sx={{
                        minHeight: 48,
                        justifyContent: open ? 'initial' : 'center',
                        px: 2,
                        borderRadius: itemBorderRadius,
                        backgroundColor: isActive ? 'transparent' : 'transparent',
                        background: isActive ? activeGradient : 'transparent',
                        boxShadow: isActive ? '0 10px 15px -3px rgba(99, 102, 241, 0.4)' : 'none',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          backgroundColor: isActive ? 'transparent' : 'rgba(255,255,255,0.05)',
                          transform: isActive ? 'scale(1.02)' : 'translateX(4px)',
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: open ? 2 : 'auto',
                          justifyContent: 'center',
                          color: isActive ? '#fff' : '#94a3b8',
                          transition: 'color 0.2s ease',
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.text}
                        sx={{
                          opacity: open ? 1 : 0,
                          transition: 'opacity 0.3s ease',
                          '& .MuiTypography-root': {
                            fontWeight: isActive ? 700 : 500,
                            fontSize: '0.9rem',
                            color: isActive ? '#fff' : '#94a3b8',
                          }
                        }}
                      />
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
              );
            })}
          </React.Fragment>
        ))}
      </List>

      <Box sx={{ flexGrow: 1 }} />

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)' }} />

      <List sx={{ px: 1.5, pb: 2 }}>
        <ListItem disablePadding sx={{ display: 'block', mb: 1 }}>
          <Tooltip title={!open ? "Sair" : ""} placement="right">
            <ListItemButton
              onClick={handleLogout}
              sx={{
                minHeight: 48,
                justifyContent: open ? 'initial' : 'center',
                px: 2,
                borderRadius: itemBorderRadius,
                '&:hover': {
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  '& .MuiListItemIcon-root, & .MuiTypography-root': { color: '#f87171' }
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 2 : 'auto',
                  justifyContent: 'center',
                  color: '#94a3b8',
                  transition: 'color 0.2s ease',
                }}
              >
                <ExitIcon />
              </ListItemIcon>
              <ListItemText
                primary="Sair"
                sx={{
                  opacity: open ? 1 : 0,
                  '& .MuiTypography-root': { fontWeight: 500, fontSize: '0.9rem', color: '#94a3b8' }
                }}
              />
            </ListItemButton>
          </Tooltip>
        </ListItem>

        {open && (
          <Box sx={{
            p: 1.5,
            borderRadius: 3,
            backgroundColor: 'rgba(255,255,255,0.03)',
            display: 'flex',
            alignItems: 'center',
            mt: 1
          }}>
            <Avatar sx={{
              background: activeGradient,
              width: 36,
              height: 36,
              mr: 1.5,
              fontSize: '0.875rem',
              fontWeight: 700,
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }}>
              {username ? username.charAt(0).toUpperCase() : 'U'}
            </Avatar>
            <Box sx={{ textAlign: 'left', overflow: 'hidden' }}>
              <Typography variant="body2" noWrap sx={{ fontWeight: 700, color: '#f8fafc' }}>
                {username}
              </Typography>
              <Typography variant="caption" display="block" noWrap sx={{ color: '#64748b', fontWeight: 500 }}>
                {nome_empresa}
              </Typography>
            </Box>
          </Box>
        )}
      </List>
    </Drawer>
  );
};

export default Navbar;
